import React, { Suspense, useMemo } from 'react';
import { sortBookingDates } from '@portal/env/utils/time/timeHelpers';
import { UpcomingBookingsCard, BookASpaceCard } from './UpcomingBookingsCard';
import {
  RowLayout,
  CardGrid,
  RowLoadingSkeleton,
} from '@portal/ui/bw-ui/dashboard/dashboardRow';
import EmptyStateCard from '@portal/ui/bw-ui/dashboard/emptyStateCard';
import {
  useUpcomingBookingsQuery,
  useTeamBookingsQuery,
} from '../hooks/useBookings';
import type { MyBookingData } from '@portal/types/nexudus/booking';
import { DateTimeIcon } from '@common/bw-ui/icons/DateTimeIcon';

const WIDGET_PROPS = {
  title: 'Upcoming Bookings',
  manageHref: '/profile/bookings',
  manageLabel: 'Manage Bookings',
} as const;

const BookingsList: React.FC<{ bookings: MyBookingData[] }> = ({
  bookings,
}) => {
  const bookingsToShow = useMemo(
    () => sortBookingDates(bookings).slice(0, 3),
    [bookings],
  );

  const isEmpty = bookingsToShow.length === 0;
  const showBookASpaceCard = bookingsToShow.length < 3;

  return (
    <RowLayout {...WIDGET_PROPS}>
      {isEmpty ? (
        <EmptyStateCard
          icon={<DateTimeIcon className="tw:w-12 tw:h-12" strokeWidth={1.8} />}
          header="No upcoming bookings within 90 days."
          message="Find and book space."
          actionLabel="Book a space"
          actionHref="/bookings/search"
        />
      ) : (
        <CardGrid>
          {bookingsToShow.map((booking) => (
            <UpcomingBookingsCard key={booking.Id} booking={booking} />
          ))}
          {showBookASpaceCard && <BookASpaceCard key="book-a-space" />}
        </CardGrid>
      )}
    </RowLayout>
  );
};

const UpcomingBookingsContent: React.FC = () => {
  const { data: upcomingBookings } = useUpcomingBookingsQuery();
  return <BookingsList bookings={upcomingBookings ?? []} />;
};

const UpcomingBookingsWithTeamContent: React.FC = () => {
  const { data: upcomingBookings } = useUpcomingBookingsQuery();
  const { data: teamBookings } = useTeamBookingsQuery();
  return (
    <BookingsList
      bookings={[...(teamBookings ?? []), ...(upcomingBookings ?? [])]}
    />
  );
};

const UpcomingBookingsWidget: React.FC<{ team?: boolean }> = ({ team }) => {
  const Content = team
    ? UpcomingBookingsWithTeamContent
    : UpcomingBookingsContent;
  return (
    <Suspense fallback={<RowLoadingSkeleton {...WIDGET_PROPS} />}>
      <Content />
    </Suspense>
  );
};

export default UpcomingBookingsWidget;
